import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardCheck,
  Upload,
  Trash2,
  Eye,
  X,
  Plus,
  FileSpreadsheet,
  ChevronDown,
  Search,
  Scale,
  Link2,
} from "lucide-react";

import { Modal, Button, Input, Select } from "../../components/common/UI";
import { apiFetch, useApp } from "../../context/AppContext";
import { confirmAction } from "../../utils/sweetAlert";
import ChecklistItemDetails from "../../components/common/ChecklistItemDetails";
import { REGION_COUNTRIES } from "../../constants/complianceRegions";

function SearchableCountrySelect({ value, onChange, countries, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const filteredCountries = countries.filter((item) =>
    item.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-800 outline-none transition hover:border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
      >
        <span className={value ? "text-slate-800" : "text-slate-400"}>
          {disabled ? "Select Region First" : value || "Select Country"}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && !disabled && (
        <div className="absolute left-0 right-0 z-[10000] mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-100 p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                autoFocus
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto p-1.5">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    onChange(item);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={`flex w-full items-center rounded-lg px-3 py-2.5 text-left text-sm transition ${
                    value === item ? "bg-blue-50 font-semibold text-blue-700" : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {item}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-slate-400">No country found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const SOURCE_LABEL = {
  platform: "Platform",
  merged: "Platform + tenant",
  tenant: "Tenant",
};

export default function TenantComplianceChecklist() {
  const navigate = useNavigate();
  const { showNotification } = useApp();
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  const [regulation, setRegulation] = useState("");
  const [mode, setMode] = useState("append");
  const [file, setFile] = useState(null);
  const [checklists, setChecklists] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [removingKey, setRemovingKey] = useState("");

  const regions = Object.keys(REGION_COUNTRIES);
  const countries = region ? REGION_COUNTRIES[region] || [] : [];

  const loadChecklists = async () => {
    try {
      const response = await apiFetch("/tenant/compliance-checklists");
      const data = await response.json();
      if (response.ok) setChecklists(data.checklists || []);
    } catch (error) {
      console.error("Unable to load checklists", error);
    }
  };

  useEffect(() => {
    loadChecklists();
  }, []);

  const resetUploadForm = () => {
    setRegion("");
    setCountry("");
    setRegulation("");
    setMode("append");
    setFile(null);
  };

  const closeModal = () => {
    if (uploading) return;
    setShowUploadModal(false);
    resetUploadForm();
  };

  const openUploadFor = (item, nextMode) => {
    setRegion(item?.region || "");
    setCountry(item?.country || "");
    setRegulation(item?.regulation || "");
    setMode(nextMode);
    setFile(null);
    setShowUploadModal(true);
  };

  const handleView = async (item) => {
    setViewLoading(true);
    setViewing({ ...item, items: [] });
    try {
      const response = await apiFetch(
        `/tenant/compliance-checklists/items?region=${encodeURIComponent(item.region)}&country=${encodeURIComponent(item.country)}`,
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Unable to load checklist");
      setViewing(data);
    } catch (error) {
      setViewing(null);
      showNotification(error.message, "error");
    } finally {
      setViewLoading(false);
    }
  };

  const handleRemoveOverlay = async (item) => {
    if (!item.overlay_item_count) return;
    const ok = await confirmAction({
      title: "Remove tenant changes?",
      text: `Remove the tenant changes for ${item.country}? The platform checklist will remain.`,
      confirmText: "Remove",
      icon: "warning",
    });
    if (!ok) return;
    try {
      setRemovingKey(`${item.region}|${item.country}`);
      const response = await apiFetch(
        `/tenant/compliance-checklists/overlay?region=${encodeURIComponent(item.region)}&country=${encodeURIComponent(item.country)}`,
        { method: "DELETE" },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Unable to remove tenant checklist");
      await loadChecklists();
      showNotification("Tenant checklist overlay removed", "success");
    } catch (error) {
      showNotification(error.message, "error");
    } finally {
      setRemovingKey("");
    }
  };

  const handleUpload = async () => {
    if (!region || !country || !file) {
      showNotification("Please select region, country and checklist file.", "error");
      return;
    }
    const formData = new FormData();
    formData.append("region", region);
    formData.append("country", country);
    formData.append("regulation", regulation);
    formData.append("mode", mode);
    formData.append("file", file);
    try {
      setUploading(true);
      const response = await apiFetch("/tenant/compliance-checklists/upload", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Checklist upload failed");
      await loadChecklists();
      resetUploadForm();
      setShowUploadModal(false);
      showNotification(mode === "replace" ? "Checklist replaced" : "Checklist items added", "success");
    } catch (error) {
      showNotification(error.message, "error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
            <ClipboardCheck className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Compliance Checklists</h1>
            <p className="mt-1 text-sm text-slate-500">
              Review checklists, then match several country laws to DPDP at the same time.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/tenant/compliance-checklists/links")}
            className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:border-blue-200 hover:text-blue-700"
          >
            <Link2 className="h-4 w-4" />
            Linked laws
          </button>
          <button
            type="button"
            onClick={() => navigate("/tenant/compliance-checklists/map")}
            className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:border-blue-200 hover:text-blue-700"
          >
            <Scale className="h-4 w-4" />
            Map to DPDP
          </button>
          <button
            type="button"
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add or replace
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="font-bold text-slate-900">Available Checklists</h2>
            <p className="mt-1 text-xs text-slate-500">
              Users see the merged result. You can add items or replace the platform list.
            </p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
            {checklists.length} {checklists.length === 1 ? "Checklist" : "Checklists"}
          </span>
        </div>

        {checklists.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center px-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
              <FileSpreadsheet className="h-7 w-7" />
            </div>
            <h3 className="mt-4 font-bold text-slate-900">No checklists yet</h3>
            <p className="mt-2 max-w-md text-sm text-slate-500">
              Super admin has not uploaded a checklist, or you can replace one for a country your organisation uses.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Region</th>
                  <th className="px-6 py-3">Country</th>
                  <th className="px-6 py-3">Regulation</th>
                  <th className="px-6 py-3">Source</th>
                  <th className="px-6 py-3">Items</th>
                  <th className="px-6 py-3">Matching laws</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {checklists.map((item) => (
                  <tr key={`${item.region}-${item.country}`} className="border-t border-slate-100 transition hover:bg-slate-50/70">
                    <td className="px-6 py-4 text-sm text-slate-700">{item.region}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900">{item.country}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{item.regulation || "-"}</td>
                    <td className="px-6 py-4">
                      <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                        {SOURCE_LABEL[item.source] || item.source}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700">
                        {item.item_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                        item.law_mapped
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                      >
                        {item.law_mapped ? "Mapped" : "Not mapped"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          title={item.law_mapped ? `Remap ${item.country} to DPDP` : `Map ${item.country} to DPDP`}
                          disabled={!item.item_count && !item.law_mapped}
                          onClick={() => navigate(`/tenant/compliance-checklists/map?region=${encodeURIComponent(item.region)}&country=${encodeURIComponent(item.country)}`)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-bold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Scale className="h-3.5 w-3.5" />
                          {item.law_mapped ? "Remap" : "Map"}
                        </button>
                        <button
                          type="button"
                          title={item.law_mapped ? `Mapped questions for ${item.country}` : "Map this country before viewing matches"}
                          disabled={!item.law_mapped}
                          onClick={() => navigate(`/tenant/compliance-checklists/links?country=${encodeURIComponent(item.country)}`)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-bold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Link2 className="h-3.5 w-3.5" />
                          Linked laws
                        </button>
                        <button
                          type="button"
                          title="View checklist"
                          onClick={() => handleView(item)}
                          className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Add items"
                          onClick={() => openUploadFor(item, "append")}
                          className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-bold text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          title="Replace checklist"
                          onClick={() => openUploadFor(item, "replace")}
                          className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-bold text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          title="Remove tenant overlay"
                          disabled={!item.overlay_item_count || removingKey === `${item.region}|${item.country}`}
                          onClick={() => handleRemoveOverlay(item)}
                          className="rounded-lg border border-slate-200 p-2 text-slate-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={showUploadModal}
        onClose={closeModal}
        size="lg"
        noPadding
        header={
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <Upload className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  {mode === "replace" ? "Replace Checklist" : "Add Checklist Items"}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {mode === "replace"
                    ? "Your file becomes the checklist users see for this country."
                    : "Your file is added after the super-admin checklist."}
                </p>
              </div>
            </div>
            <button
              type="button"
              disabled={uploading}
              onClick={closeModal}
              className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        }
      >
        <div className="p-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Region <span className="ml-1 text-red-500">*</span>
              </label>
              <Select
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  setCountry("");
                }}
              >
                <option value="">Select Region</option>
                {regions.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Country <span className="ml-1 text-red-500">*</span>
              </label>
              <SearchableCountrySelect
                value={country}
                countries={countries}
                disabled={!region}
                onChange={setCountry}
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                label="Regulation"
                value={regulation}
                onChange={(e) => setRegulation(e.target.value)}
                placeholder="Example: DPDP Act 2023"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-sm font-semibold text-slate-700">What should this file do?</label>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  { id: "append", title: "Add on", text: "Keep the super-admin items and append yours." },
                  { id: "replace", title: "Replace", text: "Hide the super-admin items and use only yours." },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setMode(option.id)}
                    className={`rounded-xl border px-4 py-3 text-left transition ${
                      mode === option.id
                        ? "border-blue-600 bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <span className="block text-sm font-bold text-slate-900">{option.title}</span>
                    <span className="mt-1 block text-xs text-slate-500">{option.text}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Checklist File <span className="ml-1 text-red-500">*</span>
              </label>
              <label className="flex cursor-pointer items-center gap-4 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-5 transition hover:border-blue-300 hover:bg-blue-50/30">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">
                    {file ? file.name : "Choose checklist file"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">CSV or XLSX with columns such as category, title, regulation</p>
                </div>
                <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700">
                  Browse
                </span>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
          <Button type="button" variant="secondary" disabled={uploading} onClick={closeModal}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={uploading}
            disabled={!region || !country || !file}
            leftIcon={<Upload className="h-4 w-4" />}
            onClick={handleUpload}
          >
            {mode === "replace" ? "Replace Checklist" : "Add Items"}
          </Button>
        </div>
      </Modal>

      <Modal
        open={Boolean(viewing)}
        onClose={() => !viewLoading && setViewing(null)}
        size="lg"
        noPadding
        header={
          <div className="flex w-full items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">{viewing?.country} Checklist</h2>
              <p className="mt-1 text-sm text-slate-500">
                {viewing?.region}
                {viewing?.regulation ? ` • ${viewing.regulation}` : ""}
                {viewing?.source ? ` • ${SOURCE_LABEL[viewing.source] || viewing.source}` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setViewing(null)}
              className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        }
      >
        <div className="max-h-[60vh] divide-y divide-slate-100 overflow-y-auto">
          {viewLoading ? (
            <p className="px-6 py-10 text-center text-sm text-slate-500">Loading checklist…</p>
          ) : (viewing?.items || []).length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-slate-500">No items in this checklist.</p>
          ) : (
            viewing.items.map((row, index) => (
              <ChecklistItemDetails key={row.id || index} item={row} index={index} />
            ))
          )}
        </div>
      </Modal>
    </div>
  );
}
